<template>
  <el-dialog
    v-model="shouldShow"
    width="720px"
    :title="`Imprimir etiqueta - ${ProductName || 'Produto'}`"
  >
    <el-descriptions class="print-descriptions" :column="12" border>
      <el-descriptions-item label="Impressora" :span="8">
        <el-select
          v-model="selectedPrinterUid"
          filterable
          placeholder="Selecione uma impressora"
          :loading="isLoadingPrinters"
        >
          <el-option
            v-for="printer in printers"
            :key="printer.uid"
            :label="PrinterLabel(printer)"
            :value="printer.uid"
          ></el-option>
        </el-select>
      </el-descriptions-item>
      <el-descriptions-item label="Porta" :span="4">
        <el-input v-model="printerPort" placeholder="9100"></el-input>
      </el-descriptions-item>
      <el-descriptions-item
        v-if="ShouldShowLabelSelect"
        label="Modelo da etiqueta"
        :span="12"
      >
        <el-select
          v-model="selectedLabelUid"
          filterable
          placeholder="Selecione um modelo"
          :loading="isLoadingLabels"
          @change="selectLabel"
        >
          <el-option
            v-for="labelOption in labels"
            :key="labelOption.uid"
            :label="labelOption.name"
            :value="labelOption.uid"
          ></el-option>
        </el-select>
      </el-descriptions-item>
      <el-descriptions-item label="Quantidade de etiquetas" :span="6">
        <el-input
          v-model="copies"
          type="number"
          min="1"
          max="100"
          placeholder="1"
        ></el-input>
      </el-descriptions-item>
      <el-descriptions-item label="Peso do produto" :span="6">
        <el-input
          v-model="weight"
          type="number"
          min="0.001"
          step="0.001"
          placeholder="0,000"
        >
          <template #append>kg</template>
        </el-input>
      </el-descriptions-item>
      <el-descriptions-item v-if="ShouldShowOrderSelect" label="Pedido" :span="12">
        <el-select
          v-model="selectedOrderUid"
          filterable
          remote
          clearable
          reserve-keyword
          placeholder="Selecione um pedido"
          :remote-method="fetchOrders"
          :loading="isLoadingOrders"
          @change="selectOrder"
        >
          <el-option
            v-for="orderOption in orders"
            :key="orderOption.uid"
            :label="OrderLabel(orderOption)"
            :value="orderOption.uid"
          ></el-option>
        </el-select>
      </el-descriptions-item>
      <el-descriptions-item v-if="ShouldShowProductSelect" label="Produto" :span="12">
        <el-select
          v-model="selectedProductUid"
          filterable
          remote
          clearable
          reserve-keyword
          placeholder="Selecione um produto"
          :remote-method="fetchProducts"
          :loading="isLoadingProducts"
          @change="selectProduct"
        >
          <el-option
            v-for="productOption in ProductOptions"
            :key="productOption.uid"
            :label="ProductLabel(productOption)"
            :value="productOption.uid"
          ></el-option>
        </el-select>
      </el-descriptions-item>
      <el-descriptions-item label="Armazenamento" :span="12">
        <el-select v-model="storageType" placeholder="Selecione">
          <el-option
            v-for="option in storageOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          ></el-option>
        </el-select>
      </el-descriptions-item>
      <el-descriptions-item label="Data de fabricação" :span="12">
        <el-date-picker
          v-model="manufacturedAt"
          type="date"
          format="DD/MM/YYYY"
          placeholder="Selecione a data"
        ></el-date-picker>
      </el-descriptions-item>
      <el-descriptions-item label="Lote" :span="12">
        <el-input v-model="productionId" placeholder="Informe o lote"></el-input>
      </el-descriptions-item>
    </el-descriptions>

    <el-alert
      class="printer-alert"
      type="warning"
      :closable="false"
      show-icon
      title="A impressão será enviada diretamente deste navegador para o IP local da impressora."
    ></el-alert>

    <label class="field-label">Pré-visualização:</label>
    <div class="label-preview" v-html="FinalHtml"></div>

    <template #footer>
      <el-row justify="space-between" type="flex">
        <el-button type="text" class="button" @click="shouldShow = false">
          Cancelar
        </el-button>
        <el-button
          type="text"
          class="button"
          :loading="isLoadingPrint"
          @click="print"
        >
          Imprimir
        </el-button>
      </el-row>
    </template>
  </el-dialog>
</template>

<script>
import LabelService from "../../services/labels.js";
import OrderService from "../../services/orders.js";
import ProductService from "../../services/products.js";
import SystemService from "../../services/system.js";
import { dateFormatter, dateFormatterShort } from "../../utils/formatters.js";
import { sendLabelToLocalPrinter } from "../../utils/label-printer.js";
import {
  LABEL_PRINT_CONTEXTS,
  LABEL_STORAGE_OPTIONS,
  LABEL_STORAGE_TYPES,
  buildMountedProducts,
  buildTemplateContext,
  buildProductMountedName,
  getPartnerName,
  interpolateTemplate,
} from "../../utils/labels.js";

export default {
  name: "LabelPrintModal",
  props: {
    label: Object,
    showModal: Boolean,
    order: Object,
    productOrder: Object,
    defaultPrintContext: {
      type: String,
      default: LABEL_PRINT_CONTEXTS.PRODUCT,
    },
  },
  emits: ["close-modal", "printed"],
  data: () => ({
    copies: 1,
    weight: null,
    manufacturedAt: null,
    productionId: "",
    storageType: LABEL_STORAGE_TYPES.CHILLED,
    printerPort: 9100,
    printers: [],
    labels: [],
    orders: [],
    products: [],
    selectedPrinterUid: null,
    selectedLabelUid: null,
    selectedLabel: null,
    selectedOrderUid: null,
    selectedProductUid: null,
    selectedOrder: null,
    selectedProduct: null,
    isLoadingPrint: false,
    isLoadingPrinters: false,
    isLoadingLabels: false,
    isLoadingOrders: false,
    isLoadingProducts: false,
  }),
  computed: {
    shouldShow: {
      get() {
        return this.showModal;
      },
      set() {
        this.$emit("close-modal");
      },
    },
    storageOptions() {
      return LABEL_STORAGE_OPTIONS;
    },
    ShouldShowLabelSelect() {
      return !this.label;
    },
    ShouldShowOrderSelect() {
      return !this.order;
    },
    ShouldShowProductSelect() {
      return !this.productOrder;
    },
    CurrentOrder() {
      return this.order || this.selectedOrder;
    },
    CurrentProduct() {
      return this.productOrder || this.selectedProduct;
    },
    ProductOptions() {
      if (this.CurrentOrder) return this.CurrentOrder.products || [];

      return this.products;
    },
    PrintContext() {
      return this.CurrentOrder
        ? LABEL_PRINT_CONTEXTS.ORDER_PRODUCT
        : LABEL_PRINT_CONTEXTS.PRODUCT;
    },
    SelectedPrinter() {
      return this.printers.find(
        (printer) => printer.uid === this.selectedPrinterUid
      );
    },
    ProductName() {
      return this.ProductLabel(this.CurrentProduct);
    },
    FinalHtml() {
      return interpolateTemplate(
        this.selectedLabel?.template,
        buildTemplateContext({
          label: this.selectedLabel,
          order:
            this.PrintContext === LABEL_PRINT_CONTEXTS.ORDER_PRODUCT
              ? this.OrderForPrint
              : null,
          product:
            this.PrintContext === LABEL_PRINT_CONTEXTS.PRODUCT
              ? this.CurrentProduct
              : null,
          printContext: this.PrintContext,
          storageType: this.storageType,
          metadata: {
            printed_by: this.printedByLabel,
            printed_at: this.printedAtLabel,
            manufactured_at: this.manufacturedAt
              ? dateFormatter.format(this.manufacturedAt)
              : "",
            production_id: this.productionId,
            copies: this.copies,
            weight: this.weight,
          },
        })
      );
    },
    printedByLabel() {
      return (
        this.CurrentProduct?.printed?.name ||
        this.CurrentOrder?.printed?.name ||
        this.$store.state.user?.name ||
        this.CurrentProduct?.printed_by ||
        this.CurrentOrder?.printed_by ||
        ""
      );
    },
    printedAtLabel() {
      if (this.CurrentProduct?.printed_at)
        return dateFormatterShort.format(new Date(this.CurrentProduct.printed_at));

      return this.CurrentOrder?.printed_at
        ? dateFormatterShort.format(new Date(this.CurrentOrder.printed_at))
        : dateFormatterShort.format(new Date());
    },
    OrderForPrint() {
      if (!this.CurrentOrder) return null;

      return {
        ...this.CurrentOrder,
        products: [
          {
            ...(this.CurrentProduct || {}),
            weight: this.weight,
          },
        ],
      };
    },
  },
  watch: {
    showModal(v) {
      if (!v) return;

      this.resetState();
      this.fetchPrinters();
      if (this.ShouldShowLabelSelect) this.fetchLabels();
      if (this.ShouldShowOrderSelect) this.fetchOrders("");
      if (this.ShouldShowProductSelect) this.fetchProducts("");
    },
  },
  methods: {
    resetState() {
      this.copies = 1;
      this.weight = this.productOrder?.weight || null;
      this.manufacturedAt = new Date();
      this.productionId =
        this.order?.production_id || String(this.order?.code || "");
      this.storageType = this.getInitialStorageType();
      this.selectedOrder = null;
      this.selectedOrderUid = null;
      this.selectedProduct = null;
      this.selectedProductUid = null;
      this.selectedLabel = this.label || null;
      this.selectedLabelUid = this.label?.uid || null;
      this.products = [];
      this.orders = this.order ? [this.order] : [];
      this.labels = this.label ? [this.label] : [];
    },
    getInitialStorageType() {
      const storage = String(
        this.productOrder?.mounted?.storage ||
          this.productOrder?.storage ||
          this.selectedProduct?.mounted?.storage ||
          this.selectedProduct?.storage ||
          ""
      ).toLowerCase();

      return storage.includes("congel")
        ? LABEL_STORAGE_TYPES.FROZEN
        : LABEL_STORAGE_TYPES.CHILLED;
    },
    PrinterLabel(printer) {
      return `${printer.name}${printer.ip ? ` - ${printer.ip}` : ""}`;
    },
    OrderLabel(order) {
      return `#${order.code || order.uid} ${getPartnerName(order.client)}`;
    },
    ProductLabel(product) {
      if (!product) return "";

      return (
        product?.name ||
        buildProductMountedName(product) ||
        buildProductMountedName(product?.mounted) ||
        product?.product?.name ||
        product?.mounted?.product?.name ||
        product.uid
      );
    },
    getFavoritePrinterUid() {
      return this.$store.state.user?.printer?.uid;
    },
    selectDefaultPrinter() {
      const favoritePrinterUid = this.getFavoritePrinterUid();
      const hasFavoritePrinter = this.printers.some(
        (printer) => printer.uid === favoritePrinterUid
      );

      if (hasFavoritePrinter) {
        this.selectedPrinterUid = favoritePrinterUid;
        return;
      }

      if (!this.selectedPrinterUid && this.printers.length === 1)
        this.selectedPrinterUid = this.printers[0].uid;
    },
    async fetchPrinters() {
      this.isLoadingPrinters = true;

      const { printers } = await SystemService().Printers().index();
      this.printers = printers || [];
      this.selectDefaultPrinter();

      this.isLoadingPrinters = false;
    },
    async fetchLabels() {
      this.isLoadingLabels = true;

      const { labels } = await LabelService().index();
      this.labels = labels || [];

      if (this.label && !this.labels.some((item) => item.uid === this.label.uid))
        this.labels.unshift(this.label);

      const selectedLabelStillExists = this.labels.some(
        (item) => item.uid === this.selectedLabelUid
      );

      if (!selectedLabelStillExists) {
        this.selectedLabel = this.labels[0] || null;
        this.selectedLabelUid = this.selectedLabel?.uid || null;
      }

      this.isLoadingLabels = false;
    },
    async fetchOrders(query) {
      if (!this.ShouldShowOrderSelect) return;

      this.isLoadingOrders = true;
      const filters = { limit: 30, include_product_mounted: true };

      if (query && Number(query)) filters.code = query;
      else if (query) filters.searchName = query;

      const { orders } = await OrderService().index(filters);
      this.orders = orders?.data || orders || [];
      this.isLoadingOrders = false;
    },
    async fetchProducts(query) {
      if (!this.ShouldShowProductSelect) return;

      if (this.CurrentOrder) return;

      this.isLoadingProducts = true;
      const { products } = await ProductService().index({
        searchName: query,
        is_normal_sell_available: true,
      });
      this.products = buildMountedProducts(products || [], true);
      this.isLoadingProducts = false;
    },
    selectLabel(uid) {
      this.selectedLabel =
        this.labels.find((label) => label.uid === uid) || null;
    },
    async selectOrder(uid) {
      this.selectedProduct = null;
      this.selectedProductUid = null;

      if (!uid) {
        this.selectedOrder = null;
        this.productionId = "";
        this.fetchProducts("");
        return;
      }

      this.isLoadingOrders = true;
      const { order } = await OrderService(uid).get();
      this.selectedOrder =
        order || this.orders.find((orderOption) => orderOption.uid === uid);
      this.productionId =
        this.selectedOrder?.production_id || String(this.selectedOrder?.code || "");

      if (this.ProductOptions.length === 1) {
        this.selectedProduct = this.ProductOptions[0];
        this.selectedProductUid = this.selectedProduct.uid;
        this.weight = this.selectedProduct?.weight || null;
        this.storageType = this.getInitialStorageType();
      }

      this.isLoadingOrders = false;
    },
    selectProduct(uid) {
      if (!uid) {
        this.selectedProduct = null;
        this.weight = null;
        return;
      }

      this.selectedProduct =
        this.ProductOptions.find((product) => product.uid === uid) || null;
      this.weight = this.selectedProduct?.weight || this.weight;
      this.storageType = this.getInitialStorageType();
    },
    async markProductAsPrinted() {
      if (!this.CurrentOrder || !this.CurrentProduct?.uid) return true;

      const payload = {
        production_id: this.productionId,
        printed_product_name: this.ProductName,
      };

      if (!this.CurrentProduct?.printed_at) payload.printed_by = true;

      const { error } = await OrderService(this.CurrentOrder?.uid)
        .Products(this.CurrentProduct.uid)
        .update(payload);

      if (error) {
        this.$notify({
          title: "Não foi possível marcar o produto como impresso",
          message: error?.message,
          type: "warning",
          position: "bottom-right",
        });
        return false;
      }

      this.$emit("printed");
      return true;
    },
    validate() {
      this.copies = Math.min(Math.max(Number(this.copies) || 1, 1), 100);

      if (!this.selectedLabel) {
        this.$notify({
          title: "Selecione um modelo de etiqueta",
          type: "error",
          position: "bottom-right",
        });
        return false;
      }

      if (!this.SelectedPrinter) {
        this.$notify({
          title: "Nenhuma impressora padrão disponível",
          message: "Defina uma impressora no usuário ou cadastre uma impressora ativa",
          type: "error",
          position: "bottom-right",
        });
        return false;
      }

      if (!this.CurrentProduct) {
        this.$notify({
          title: "Selecione um produto",
          type: "error",
          position: "bottom-right",
        });
        return false;
      }

      if (!Number(this.weight)) {
        this.$notify({
          title: "Informe o peso do produto",
          type: "error",
          position: "bottom-right",
        });
        return false;
      }

      if (!this.manufacturedAt) {
        this.$notify({
          title: "Informe a data de fabricação",
          type: "error",
          position: "bottom-right",
        });
        return false;
      }

      if (!this.productionId) {
        this.$notify({
          title: "Informe o lote",
          type: "error",
          position: "bottom-right",
        });
        return false;
      }

      return true;
    },
    async print() {
      if (!this.validate()) return;

      this.isLoadingPrint = true;

      try {
        const markPrintedPromise = this.markProductAsPrinted();
        const result = await sendLabelToLocalPrinter({
          printer: this.SelectedPrinter,
          port: this.printerPort,
          copies: this.copies,
          content: this.FinalHtml,
          orientation: this.selectedLabel?.orientation,
          paperWidth: this.selectedLabel?.paper_width,
        });

        await markPrintedPromise;

        this.$notify({
          title: result.message,
          type: "success",
          position: "bottom-right",
        });
        if (result.warnings?.length) {
          this.$notify({
            title: "Estilos não suportados na impressão",
            message: result.warnings.join(" "),
            type: "warning",
            position: "bottom-right",
          });
        }
        this.$emit("close-modal");
      } catch (error) {
        this.$notify({
          title: "Não foi possível imprimir a etiqueta",
          message: error?.message || "Verifique a impressora e a rede local",
          type: "error",
          position: "bottom-right",
        });
      }

      this.isLoadingPrint = false;
    },
  },
};
</script>

<style scoped>
.field-label {
  display: block;
  margin: 16px 0 6px;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
}

.label-preview {
  min-height: 180px;
  padding: 16px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  overflow: auto;
}

.printer-alert {
  margin-top: 12px;
  margin-bottom: 12px;
}

:deep(.print-descriptions .el-input),
:deep(.print-descriptions .el-select),
:deep(.print-descriptions .el-date-editor) {
  width: 100%;
}
</style>
